import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

export interface AnalyticsHeaderProps {
  siteTitle?: string;
  siteUrl?: string | null;
  startDate: Date;
  endDate: Date;
  comparisonStartDate: Date;
  comparisonEndDate: Date;
  viewType: 'daily' | 'monthly';
  onBack: () => void;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onComparisonStartDateChange: (date: Date) => void;
  onComparisonEndDateChange: (date: Date) => void;
  onViewTypeChange: (viewType: 'daily' | 'monthly') => void;
  onResetData: () => Promise<void>;
}

type PeriodType = 'last7days' | 'last30days' | 'last3months' | 'thisMonth' | 'lastMonth' | 'lastYear' | 'custom';

const PERIOD_OPTIONS = [
  { value: 'last7days', label: '直近7日' },
  { value: 'last30days', label: '直近30日' },
  { value: 'last3months', label: '直近3ヶ月' },
  { value: 'thisMonth', label: '今月' },
  { value: 'lastMonth', label: '前月' },
  { value: 'lastYear', label: '直近1年' },
  { value: 'custom', label: 'カスタム' },
] as const;

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const calculatePeriodDates = (period: PeriodType): { start: Date; end: Date } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start: Date;
  let end: Date = new Date(today);
  end.setHours(23, 59, 59, 999);

  switch (period) {
    case 'last7days':
      start = new Date(today);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case 'last30days':
      start = new Date(today);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case 'last3months':
      start = new Date(today);
      start.setMonth(start.getMonth() - 3);
      start.setDate(start.getDate() + 1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'thisMonth':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'lastMonth':
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      start = new Date(lastMonth);
      start.setHours(0, 0, 0, 0);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'lastYear':
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      start = new Date(today);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
  }

  return { start, end };
};

const calculateComparisonPeriodDates = (
  period: PeriodType,
  displayStartDate: Date,
  displayEndDate: Date
): { start: Date; end: Date } => {
  if (period === 'lastMonth' || period === 'thisMonth' || period === 'last7days' || period === 'last30days' || period === 'last3months' || period === 'lastYear') {
    return calculatePeriodDates(period);
  }

  const displayDuration = displayEndDate.getTime() - displayStartDate.getTime();
  const end = new Date(displayStartDate);
  end.setTime(end.getTime() - 1);
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setTime(start.getTime() - displayDuration);
  start.setHours(0, 0, 0, 0);

  return { start, end };
};

export const AnalyticsHeader: React.FC<AnalyticsHeaderProps> = ({
  siteTitle,
  siteUrl,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  viewType,
  onBack,
  onStartDateChange,
  onEndDateChange,
  onComparisonStartDateChange,
  onComparisonEndDateChange,
  onViewTypeChange,
  onResetData,
}) => {
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [displayPeriod, setDisplayPeriod] = useState<PeriodType>('thisMonth');
  const [comparisonPeriod, setComparisonPeriod] = useState<PeriodType>('lastMonth');

  const handleResetClick = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = async () => {
    try {
      setResetting(true);
      await onResetData();
      setResetDialogOpen(false);
    } catch (error) {
      alert('データのリセットに失敗しました');
    } finally {
      setResetting(false);
    }
  };

  const handleDisplayPeriodChange = (event: SelectChangeEvent<PeriodType>) => {
    const period = event.target.value as PeriodType;
    setDisplayPeriod(period);
    if (period !== 'custom') {
      const { start, end } = calculatePeriodDates(period);
      onStartDateChange(start);
      onEndDateChange(end);
      
      if (comparisonPeriod !== 'custom') {
        const { start: compStart, end: compEnd } = calculateComparisonPeriodDates(comparisonPeriod, start, end);
        onComparisonStartDateChange(compStart);
        onComparisonEndDateChange(compEnd);
      }
    }
  };

  const handleComparisonPeriodChange = (event: SelectChangeEvent<PeriodType>) => {
    const period = event.target.value as PeriodType;
    setComparisonPeriod(period);
    if (period !== 'custom') {
      const { start, end } = calculateComparisonPeriodDates(period, startDate, endDate);
      onComparisonStartDateChange(start);
      onComparisonEndDateChange(end);
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);
    onStartDateChange(date);
    setDisplayPeriod('custom');
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);
    onEndDateChange(date);
    setDisplayPeriod('custom');
  };

  const handleComparisonStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);
    onComparisonStartDateChange(date);
    setComparisonPeriod('custom');
  };

  const handleComparisonEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);
    onComparisonEndDateChange(date);
    setComparisonPeriod('custom');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 3,
          pb: 2,
          backgroundColor: '#F5F5F5',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={onBack}
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '50%',
              width: 40,
              height: 40,
              color: '#000000',
              padding: 0,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#000000' }}>
            データ分析
          </Typography>
          {siteTitle && (
            <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 600 }}>
              : {siteTitle}
            </Typography>
          )}
          {siteUrl && (
            <Link
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: '14px',
                color: '#1B76D2',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {siteUrl}
            </Link>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 3, mb: 3 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#000000', mb: 1.5 }}>
          表示期間
        </Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={displayPeriod}
              onChange={handleDisplayPeriodChange}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                height: '48px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D0D0D0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1B76D2',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1B76D2',
                },
              }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            value={formatDateForInput(startDate)}
            onChange={handleStartDateChange}
            disabled={displayPeriod !== 'custom'}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: displayPeriod === 'custom' ? '#E7E7E7' : '#F5F5F5',
                borderRadius: '12px',
                height: '48px',
                '& fieldset': {
                  border: displayPeriod === 'custom' ? 'none' : '1px solid #E0E0E0',
                },
                '& input': {
                  padding: '12px 16px',
                  fontSize: '14px',
                  height: '48px',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F5F5F5',
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                },
              },
            }}
          />
          <Typography sx={{ color: '#000000' }}>~</Typography>
          <TextField
            type="date"
            value={formatDateForInput(endDate)}
            onChange={handleEndDateChange}
            disabled={displayPeriod !== 'custom'}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: displayPeriod === 'custom' ? '#E7E7E7' : '#F5F5F5',
                borderRadius: '12px',
                height: '48px',
                '& fieldset': {
                  border: displayPeriod === 'custom' ? 'none' : '1px solid #E0E0E0',
                },
                '& input': {
                  padding: '12px 16px',
                  fontSize: '14px',
                  height: '48px',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F5F5F5',
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                },
              },
            }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <Select
              value={viewType}
              onChange={(e) => onViewTypeChange(e.target.value as 'daily' | 'monthly')}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                height: '48px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D0D0D0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1B76D2',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1B76D2',
                },
              }}
            >
              <MenuItem value="daily">日次</MenuItem>
              <MenuItem value="monthly">月次</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ px: 3, mb: 3 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#000000', mb: 1.5 }}>
          比較期間
        </Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={comparisonPeriod}
              onChange={handleComparisonPeriodChange}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                height: '48px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D0D0D0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1B76D2',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1B76D2',
                },
              }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            value={formatDateForInput(comparisonStartDate)}
            onChange={handleComparisonStartDateChange}
            disabled={comparisonPeriod !== 'custom'}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: comparisonPeriod === 'custom' ? '#E7E7E7' : '#F5F5F5',
                borderRadius: '12px',
                height: '48px',
                '& fieldset': {
                  border: comparisonPeriod === 'custom' ? 'none' : '1px solid #E0E0E0',
                },
                '& input': {
                  padding: '12px 16px',
                  fontSize: '14px',
                  height: '48px',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F5F5F5',
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                },
              },
            }}
          />
          <Typography sx={{ color: '#000000' }}>~</Typography>
          <TextField
            type="date"
            value={formatDateForInput(comparisonEndDate)}
            onChange={handleComparisonEndDateChange}
            disabled={comparisonPeriod !== 'custom'}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: comparisonPeriod === 'custom' ? '#E7E7E7' : '#F5F5F5',
                borderRadius: '12px',
                height: '48px',
                '& fieldset': {
                  border: comparisonPeriod === 'custom' ? 'none' : '1px solid #E0E0E0',
                },
                '& input': {
                  padding: '12px 16px',
                  fontSize: '14px',
                  height: '48px',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F5F5F5',
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                },
              },
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={resetDialogOpen}
        onClose={() => !resetting && setResetDialogOpen(false)}
      >
        <DialogTitle>アナリティクスデータのリセット</DialogTitle>
        <DialogContent>
          <DialogContentText>
            このサイトの全てのアナリティクスデータ（イベント・セッション）を削除します。
            この操作は取り消せません。本当に実行しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetting}>
            キャンセル
          </Button>
          <Button
            onClick={handleResetConfirm}
            color="error"
            variant="contained"
            disabled={resetting}
          >
            {resetting ? 'リセット中...' : 'リセット'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
